// Van Life — rules engine: dealing, trick resolution, zone/challenge evaluation,
// match results, and valid-action enumeration. Pure functions only. See RULES.md.

import {
  Card, Suit, Rank, ZoneCardId, ChallengeCardId,
  CompletedTrick, MatchResult, VanLifeMatchState, VanLifePlayer, VanLifePark,
  VanLifeState, VanLifeAction,
} from './types';
import { SUITS, DEAL_SIZE, HAND_SIZE, PARK_ADJACENCY } from './data';
import { shuffle } from './rng';

// ── Deck helpers ──────────────────────────────────────────────────────────────

export function createBaseDeck(): Card[] {
  const cards: Card[] = [];
  for (const suit of SUITS) {
    for (let rank = 1; rank <= 9; rank++) {
      cards.push({ suit, rank: rank as Rank, id: `${suit}-${rank}` });
    }
  }
  return cards;
}

export function makeWild(rank: Rank): Card {
  return { suit: 'Wild', rank, id: `Wild-${rank}` };
}

// Returns all k-element combinations of arr (order within combo irrelevant)
export function combinations<T>(arr: T[], k: number): T[][] {
  if (k === 0) return [[]];
  if (arr.length < k) return [];
  const [first, ...rest] = arr;
  return [
    ...combinations(rest, k - 1).map(combo => [first, ...combo]),
    ...combinations(rest, k),
  ];
}

// Variant: advance deck cards until finding one that matches requiredSuit (or is Wild).
// Cards skipped are discarded (consumed). Returns [playedCard, remainingDeck].
export function drawDeckToSuit(currentCard: Card, parkDeck: Card[], requiredSuit: Suit): [Card, Card[]] {
  if (currentCard.suit === requiredSuit || currentCard.suit === 'Wild') {
    return [currentCard, parkDeck];
  }
  let remaining = [...parkDeck];
  while (remaining.length > 0) {
    const candidate = remaining[0];
    remaining = remaining.slice(1);
    if (candidate.suit === requiredSuit || candidate.suit === 'Wild') {
      return [candidate, remaining];
    }
  }
  // Deck exhausted without finding a valid card — plays original (loses, wrong suit)
  return [currentCard, []];
}

// ── Zone evaluator ────────────────────────────────────────────────────────────

export function checkZone(id: ZoneCardId, final: number, history: number[]): boolean {
  switch (id) {
    case 'summit-exact':  return final === 4;
    case 'momentum':      return final <= 2 || final >= 6;
    case 'tundra-winds':  return final >= 2 && final <= 4;
    case 'coastal-drift': return final >= 4 && final <= 6;
    case 'ridge-line':    return final >= 3 && final <= 5;
    case 'river-delta':   return final >= 2 && final <= 5;
    case 'safe-passage':  return !history.some(p => p === 1 || p === 7);
    case 'divergence':    return final <= 3 || final >= 5;
    case 'wilderness':    return final >= 2 && final <= 6;
    case 'prairie-wind':  return final >= 1 && final <= 5;
    case 'sea-breeze':    return final >= 3 && final <= 7;
    case 'open-country':  return final >= 2 && final <= 7;
  }
}

// ── Challenge evaluator ───────────────────────────────────────────────────────

export function checkChallenge(
  id: ChallengeCardId,
  playerId: string,
  tricks: CompletedTrick[],
  deckTrickCount: number,
): boolean {
  const myWins   = tricks.filter(t => t.winnerId === playerId);
  const wonNums  = myWins.map(t => t.trickNumber);
  const winCards = myWins.map(t => t.winnerCard);
  const total    = tricks.length;
  const half     = Math.floor(total / 2);

  switch (id) {
    case 'first-blood':
      return wonNums.includes(1);

    case 'last-stand':
      return wonNums.includes(total);

    case 'bookends':
      return wonNums.includes(1) && wonNums.includes(total);

    case 'alternating': {
      if (myWins.length === 0) return false;
      const set = new Set(wonNums);
      return wonNums.every(n => !set.has(n + 1));
    }

    case 'streak': {
      let run = 1;
      for (let i = 1; i < wonNums.length; i++) {
        run = wonNums[i] === wonNums[i - 1] + 1 ? run + 1 : 1;
        if (run >= 3) return true;
      }
      return run >= 3;
    }

    case 'low-road':
      return winCards.some(c => c.rank <= 2);

    case 'suit-sampler': {
      const suits = new Set(winCards.filter(c => c.suit !== 'Wild').map(c => c.suit));
      return suits.has('Green') && suits.has('Blue') && suits.has('Tan');
    }

    case 'strong-finish': {
      const first  = myWins.filter(t => t.trickNumber <= half).length;
      const second = myWins.filter(t => t.trickNumber > half).length;
      return second > first;
    }

    case 'deck-beater':
      return myWins.length > deckTrickCount;

    case 'odd-tricks':
      return myWins.length > 0 && wonNums.every(n => n % 2 === 1);

    case 'iron-patience':
      return !wonNums.some(n => n <= 3);

    case 'clean-sweep':
      return myWins.some(trick => {
        const mine = trick.cards[playerId];
        if (!mine || mine.suit === 'Wild') return false;
        return Object.entries(trick.cards)
          .filter(([id]) => id !== playerId)
          .every(([, c]) => c.suit !== mine.suit);
      });

    case 'double-run': {
      let run = 1;
      for (let i = 1; i < wonNums.length; i++) {
        run = wonNums[i] === wonNums[i - 1] + 1 ? run + 1 : 1;
        if (run >= 2) return true;
      }
      return run >= 2;
    }

    case 'pack-light':
      return myWins.length >= 2 && myWins.length <= 5;

    case 'first-or-last':
      return wonNums.includes(1) || wonNums.includes(total);

    case 'low-gear':
      return winCards.some(c => c.rank <= 4);

    case 'early-bird':
      return wonNums.some(n => n <= half);

    case 'night-owl':
      return wonNums.some(n => n > half);

    case 'pioneer-spirit':
      return myWins.length >= 1;
  }
}

// ── Trick resolution ──────────────────────────────────────────────────────────

export function resolveTrick(
  leadCard: Card,
  followCard: Card,
  deckCard: Card | null,
  leadId: string,
  followId: string,
  deckLeads: boolean,
): string | 'deck' {
  const candidates: Array<{ id: string | 'deck'; card: Card }> = [
    { id: leadId, card: leadCard },
    { id: followId, card: followCard },
  ];
  // Deck exhausted (null): players-only trick — deck cannot win.
  if (deckCard) candidates.push({ id: 'deck', card: deckCard });

  const wilds = candidates.filter(c => c.card.suit === 'Wild');
  if (wilds.length > 0) {
    return wilds.reduce((best, c) => c.card.rank > best.card.rank ? c : best).id;
  }

  // Led suit is deck's suit when deck leads; lead player's suit otherwise.
  const ledSuit = deckLeads && deckCard ? deckCard.suit : leadCard.suit;
  const inSuit = candidates.filter(c => c.card.suit === ledSuit);
  return inSuit.reduce((best, c) => c.card.rank > best.card.rank ? c : best).id;
}

// ── Deal ──────────────────────────────────────────────────────────────────────

export function dealMatch(
  matchNumber: number,
  players: VanLifePlayer[],
  wildPool: Card[],
  seed: number,
): [VanLifeMatchState, number] {
  const base = createBaseDeck();
  let deck = [...base, ...wildPool];
  let s = seed;
  [deck, s] = shuffle(deck, s);

  // Deal DEAL_SIZE cards to each player; remainder goes to park deck
  const hands: Record<string, Card[]> = {};
  let idx = 0;
  for (const p of players) {
    hands[p.id] = deck.slice(idx, idx + DEAL_SIZE);
    idx += DEAL_SIZE;
  }
  const parkDeck = deck.slice(idx);

  const p1Id = players.find(p => p.playerNumber === 1)!.id;

  // Flip opening deck card immediately
  const currentDeckCard = parkDeck[0];
  const remainingDeck   = parkDeck.slice(1);

  return [
    {
      matchNumber,
      totalTricks: HAND_SIZE, // both players discard to HAND_SIZE before tricks begin
      hands,
      parkDeck: remainingDeck,
      currentDeckCard,
      deckLeads: true,
      trickNumber: 1,
      leadPlayerId: p1Id,
      leadCard: null,
      followCard: null,
      subPhase: 'discard',
      pendingDiscards: {},
      completedTricks: [],
      tracker: 4,
      trackerHistory: [4],
      deckTrickCount: 0,
      result: null,
    },
    s,
  ];
}

// ── Match result evaluation ───────────────────────────────────────────────────

// P1 = Track Player: conquers park by meeting zone condition.
// P2 = Challenge Player: conquers park by meeting challenge condition.
// Same park: either player clearing their condition counts (more permissive).
export function evaluateResult(
  players: VanLifePlayer[],
  parks: VanLifePark[],
  tricks: CompletedTrick[],
  tracker: number,
  trackerHistory: number[],
  deckTrickCount: number,
): MatchResult {
  const p1 = players.find(p => p.playerNumber === 1)!; // track player
  const p2 = players.find(p => p.playerNumber === 2)!; // challenge player
  const samePark = p1.currentParkId === p2.currentParkId;
  const parkResults: MatchResult['parkResults'] = [];

  if (samePark) {
    const park = parks.find(p => p.id === p1.currentParkId)!;
    const zoneMet      = checkZone(park.zoneCardId, tracker, trackerHistory);
    const challengeMet = checkChallenge(park.challengeCardId, p2.id, tricks, deckTrickCount);
    const conquered = zoneMet || challengeMet;
    for (const pl of players) {
      parkResults.push({ parkId: park.id, playerId: pl.id, zoneMet, challengeMet, conquered });
    }
  } else {
    const p1Park         = parks.find(p => p.id === p1.currentParkId)!;
    const p1ZoneMet      = checkZone(p1Park.zoneCardId, tracker, trackerHistory);
    const p1ChallengeMet = checkChallenge(p1Park.challengeCardId, p1.id, tricks, deckTrickCount);
    parkResults.push({ parkId: p1Park.id, playerId: p1.id, zoneMet: p1ZoneMet, challengeMet: p1ChallengeMet, conquered: p1ZoneMet });

    const p2Park         = parks.find(p => p.id === p2.currentParkId)!;
    const p2ZoneMet      = checkZone(p2Park.zoneCardId, tracker, trackerHistory);
    const p2ChallengeMet = checkChallenge(p2Park.challengeCardId, p2.id, tricks, deckTrickCount);
    parkResults.push({ parkId: p2Park.id, playerId: p2.id, zoneMet: p2ZoneMet, challengeMet: p2ChallengeMet, conquered: p2ChallengeMet });
  }

  return { trackerFinal: tracker, deckTrickCount, parkResults };
}

// ── getValidActions ───────────────────────────────────────────────────────────

export function getValidActions(state: VanLifeState, playerId: string): VanLifeAction[] {
  if (state.phase === 'movement') {
    if (state.pendingMoves[playerId] !== undefined) return [];
    const player = state.players.find(p => p.id === playerId);
    if (!player) return [];
    const adj = PARK_ADJACENCY[player.currentParkId] ?? [];
    return [player.currentParkId, ...adj].map(parkId => ({
      type: 'chooseMove' as const, playerId, targetParkId: parkId,
    }));
  }

  if (state.phase === 'trick-taking' && state.match) {
    const m = state.match;

    // Discard phase: each player selects DEAL_SIZE - HAND_SIZE cards to discard
    if (m.subPhase === 'discard') {
      if (m.pendingDiscards[playerId]) return [];
      const hand = m.hands[playerId] ?? [];
      const n = DEAL_SIZE - HAND_SIZE;
      return combinations(hand, n).map(cards => ({
        type: 'discardCards' as const,
        playerId,
        cardIds: cards.map(c => c.id),
      }));
    }

    const followId = state.players.find(p => p.id !== m.leadPlayerId)?.id;
    const myTurn =
      (m.subPhase === 'lead'   && m.leadPlayerId === playerId) ||
      (m.subPhase === 'follow' && followId === playerId);
    if (!myTurn) return [];

    const hand = m.hands[playerId] ?? [];
    let playable = hand;

    if (m.deckLeads && m.currentDeckCard) {
      const deckSuit = m.currentDeckCard.suit;
      if (deckSuit !== 'Wild') {
        const hasSuit = hand.some(c => c.suit === deckSuit);
        if (hasSuit) playable = hand.filter(c => c.suit === deckSuit || c.suit === 'Wild');
      }
    } else if (m.subPhase === 'follow') {
      const leadSuit = m.leadCard?.suit;
      if (leadSuit && leadSuit !== 'Wild') {
        const hasSuit = hand.some(c => c.suit === leadSuit);
        if (hasSuit) playable = hand.filter(c => c.suit === leadSuit || c.suit === 'Wild');
      }
    }

    return playable.map(c => ({ type: 'playCard' as const, playerId, cardId: c.id }));
  }

  if (state.phase === 'match-result') {
    const p1 = state.players.find(p => p.playerNumber === 1);
    if (p1?.id === playerId) return [{ type: 'advanceMatch', playerId }];
  }

  return [];
}
